'use client'

import * as React from 'react'
import Link from 'next/link'

import { cn } from '@/lib/utils'
import { ChromaText } from '@/components/ui/chroma-text'

/* Shared primitives for the bespoke product pages (products/udc, products/hrms,
   …). Everything here is presentation only — no copy lives in this file, so a
   page can never pick up wording that is not in its own data source. */

/** The site's single easing curve. */
export const EASE = 'cubic-bezier(0.22,1,0.36,1)'

/** Hairline used for every rule — the homepage trust-strip weight. */
export const HAIRLINE = 'border-border/70'

/* Panel surfaces. Cards sit on the page colour behind a hairline — grid tiles
   read as outlined regions of the canvas, not grey slabs (sitewide decision:
   grids use bg-background, never a muted wash). Only the ACTIVE state keeps a
   muted tint, as the deliberate highlight against its resting siblings. */
export const PANEL = 'border border-border/70 bg-background'
export const PANEL_RESTING = 'border border-border/70 bg-background'
export const PANEL_ACTIVE = 'border border-border/70 bg-muted dark:bg-muted/80'

/**
 * Restores em-dashes that were lost from the source copy in products.ts, where
 * several strings carry a bare double space in place of a dash — e.g.
 * "All employee data  documents, history, roles  in one secure record".
 * HTML collapses that run to a single space, which reads as a grammatical
 * error. This repairs the punctuation at display time and changes nothing else;
 * the fix belongs in products.ts, and this can go once it lands there.
 */
export function restoreDashes(text: string): string {
  return text.replace(/ {2,}/g, ', ')
}

/**
 * One-shot in-view flag. Re-exported from the shared implementation so every
 * page in the app reveals through the same fail-safe path — the local copy
 * that used to live here gated on an intersection RATIO, which strands any
 * section taller than a few viewports at opacity 0 forever. See
 * `@/lib/hooks/use-reveal` for the full account.
 */
export { useReveal } from '@/lib/hooks/use-reveal'

/**
 * Eyebrow: a hairline rule and uppercase text. Deliberately NOT the tinted pill
 * the shared product template repeats four times per page.
 */
export function Eyebrow({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <p
      className={cn(
        'flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground',
        className
      )}
    >
      <span
        aria-hidden
        className="h-px w-6 shrink-0 bg-emerald-600/60 dark:bg-primary-active/50"
      />
      {children}
    </p>
  )
}

/**
 * Section header in the homepage's grammar: eyebrow, heading left, deck under
 * it, optional action pinned right on large screens.
 */
export function SectionHead({
  eyebrow,
  title,
  deck,
  action,
  className,
}: {
  eyebrow: string
  title: React.ReactNode
  deck?: React.ReactNode
  action?: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between lg:gap-12',
        className
      )}
    >
      <div className="lg:max-w-2xl">
        <Eyebrow>{eyebrow}</Eyebrow>

        <h2 className="font-heading mt-4 text-2xl font-normal leading-tight text-foreground sm:text-3xl md:text-4xl lg:text-5xl">
          <ChromaText>{title}</ChromaText>
        </h2>

        {deck && (
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:mt-4 sm:text-base md:text-[17px]">
            {deck}
          </p>
        )}
      </div>

      {action && <div className="lg:shrink-0 lg:pb-1">{action}</div>}
    </div>
  )
}

/**
 * A link set INSIDE a paragraph. QuietLink is a standalone action — it is
 * inline-flex and carries a chevron, so dropping it mid-sentence breaks the
 * line box. This is the prose equivalent: accent underline, inherited size.
 */
export function InlineLink({
  href,
  children,
  className,
}: {
  href: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <Link
      href={href}
      className={cn(
        'font-medium text-foreground underline underline-offset-4 transition-colors',
        'decoration-emerald-600/40 hover:decoration-emerald-600',
        'dark:decoration-primary-active/40 dark:hover:decoration-primary-active',
        'focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2',
        'focus-visible:outline-emerald-600 dark:focus-visible:outline-primary-active',
        className
      )}
    >
      {children}
    </Link>
  )
}

/** Text link with the homepage's underline-sweep hover. */
export function QuietLink({
  href,
  children,
  className,
}: {
  href: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <a
      href={href}
      className={cn(
        'group inline-flex items-center gap-2 text-sm font-medium text-foreground transition-colors sm:text-base',
        'focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-4',
        'focus-visible:outline-emerald-600 dark:focus-visible:outline-primary-active',
        className
      )}
    >
      <span className="relative">
        {children}
        <span
          aria-hidden
          className="absolute bottom-0 left-0 h-0.5 w-0 bg-emerald-600 transition-all duration-300 group-hover:w-full dark:bg-primary-active"
        />
      </span>

      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden
        className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 sm:h-5 sm:w-5"
      >
        <path
          d="M6 4l8 8-8 8"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </a>
  )
}
